import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Typography,
  Button,
  Grid,
  Card,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_MAP_TR = { "monday": "P.tesi", "tuesday": "Salı", "wednesday": "Çarş.", "thursday": "Perş.", "friday": "Cuma", "saturday": "C.tesi", "sunday": "Pazar" };
const STATUS_LABELS = { '3': 'Onaylandı', '4': 'Onay Bekleniyor' };
const VAT_RATE = 0.2;

const getPriceWithoutVat = (price) => {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) return null;
  return numericPrice / (1 + VAT_RATE);
};

const formatPriceWithoutVat = (price, currency) => {
  const netPrice = getPriceWithoutVat(price);
  if (netPrice === null) return '-';
  const currencyLabel = currency === 'TRY' ? '₺' : currency || '';
  return currencyLabel ? `${netPrice.toFixed(2)} ${currencyLabel}` : netPrice.toFixed(2);
};

export default function MallManagementGift() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: giftPermission, loading: permissionLoading } = useServicePermission(SERVICES.GIFT);

  const [giftList, setGiftList] = useState([]);
  const [startFilter, setStartFilter] = useState(moment());
  const [endFilter, setEndFilter] = useState(moment());
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isImageModalOpen, setImageModalOpen] = useState(false);

  const handleOpenImageModal = () => setImageModalOpen(true);
  const handleCloseImageModal = () => setImageModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Hediye Paketi',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const fetchDashboardData = async () => {
      if (!mallID) { setLoading(false); return; }
      setLoading(true);
      try {
        const userHasPermission = giftPermission;
        setHasPermission(userHasPermission);
        if (userHasPermission) {
          const response = await Services.GetLiveAnalyticsGift(parsedUser.token, mallID);
          if (response.data && response.data.result) {
            setAnalyticsData(response.data.result);
            setGiftList(response.data.result.giftList || []);
          } else {
            setAnalyticsData({});
            setGiftList([]);
          }
        } else {
          setAnalyticsData({});
          setGiftList([]);
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
        setGiftList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [mallID, parsedUser?.token, giftPermission, permissionLoading]);

  const getLiveAnalyticsGiftList = () => {
    if (!hasPermission) return;
    const startDate = startFilter.format('YYYY-MM-DD');
    const endDate = endFilter.format('YYYY-MM-DD');
    Services.GetLiveAnalyticsGiftList(parsedUser.token, mallID, startDate, endDate)
      .then((res) => {
        let list = res.data.result || [];
        if (statusFilter) {
          list = list.filter(r => STATUS_LABELS[r.status] === statusFilter);
        }
        setGiftList(list);
      })
      .catch((err) => console.error('GiftList yüklenirken hata:', err));
  };

  const handleClearFilters = () => {
    if (!hasPermission) return;
    setStartFilter(moment());
    setEndFilter(moment());
    setStatusFilter('');
    setGiftList(analyticsData?.giftList || []);
  };

  useEffect(() => { setPage(1); }, [giftList]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const paginatedRows = giftList.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const defaultGiftCurrency = analyticsData?.priceList?.daily?.currency || 'TRY';
  const maskName = (name) => {
    if (!name || name.length === 0) return '*******';
    const parts = name.split(' ');
    return parts.map(part => part.length > 1 ? part.charAt(0) + '*'.repeat(part.length - 1) : part).join(' ');
  };

  const handleExportExcel = () => {
    if (!hasPermission || giftList.length === 0) {
      toast.warn('Export edilecek veri bulunamadı.');
      return;
    }

    const exportData = giftList.map((r) => ({
      'NO': r.id,
      'AVM': customer?.customer?.title || 'AVM',
      'MÜŞTERİ ADI': maskName(r.billing?.name) || '-',
      'MÜŞTERİ SOYAD': maskName(r.billing?.lastname) || '-',
      'PAKET NO': r.requestID || '-',
      'PAKET ADEDİ': r.packageCount || 0,
      'FİYAT (KDV HARİÇ)': formatPriceWithoutVat(r.price, r.currency || defaultGiftCurrency),
      'OLUŞTURMA TARİHİ': new Date(Number(r.createdCateTime) * 1000).toLocaleString('tr-TR'),
      'DURUMU': STATUS_LABELS[r.status] || 'Bilinmiyor',
    }));

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        'NO': 'number',
        'AVM': 'string',
        'MÜŞTERİ ADI': 'string',
        'MÜŞTERİ SOYAD': 'string',
        'PAKET NO': 'string',
        'PAKET ADEDİ': 'number',
        'FİYAT (KDV HARİÇ)': 'string',
        'OLUŞTURMA TARİHİ': 'string',
        'DURUMU': 'string',
      },
      sheetTitle: `Hediye_Paketi_Listesi_${moment().format('YYYY-MM-DD')}`
    })
      .then((response) => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error('Excel export hatası:', error);
        toast.error(error.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
      });
  };

  const now = new Date();
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const currentMonthAbbrev = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(now);

  const dailyHeatmapChart = useMemo(() => {
    const data = analyticsData?.monthlyDailyHourlyUsageTotalCount ?? {};
    const allTimeSlots = Array.from({ length: 16 }, (_, i) => { const h = 8 + i, n = h + 1; return `${h.toString().padStart(2, '0')}:00-${n === 24 ? '00' : n.toString().padStart(2, '0')}:00`; });

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return DAY_ORDER.reduce((sum, dayKey) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = DAY_ORDER.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const series = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...DAY_ORDER.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: data[dayKey]?.[timeSlot] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(data).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const colors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const options = {
      chart: { type: 'heatmap', toolbar: { show: false } },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: colors[0] },
              { from: 1, to: Math.floor(maxCount * 0.25), color: colors[2] },
              { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: colors[3] },
              { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: colors[4] }
            ]
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      stroke: { show: true, width: 3, colors: ['#fff'] },
      xaxis: { type: 'category' },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => `${value} adet` } }
    };
    return { series, options, maxCount, colors };
  }, [analyticsData]);

  const monthlyUsageChartData = useMemo(() => {
    const income = analyticsData?.monthlyIncome || {};
    const weekRanges = Object.keys(income);
    const categories = weekRanges.map((range) => `${currentMonthAbbrev} ${range.includes('rest') ? `${range.split('-')[0]}-${lastDayOfMonth}` : range}`);
    const usageData = weekRanges.map((key) => income[key]?.usage || 0);
    return { series: [{ name: 'Kullanım Adedi', data: usageData }], categories };
  }, [analyticsData, currentMonthAbbrev, lastDayOfMonth]);

  const monthlyUsageChartOptions = { chart: { type: 'bar', toolbar: { show: false } }, plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } }, colors: ['#1C64F2'], xaxis: { categories: monthlyUsageChartData.categories }, yaxis: { title: { text: 'Kullanım Adedi' }, labels: { formatter: (val) => val.toFixed(0) } }, legend: { position: 'bottom' }, dataLabels: { enabled: false } };

  const weeklyComboChartData = useMemo(() => {
    const weeklyIncome = analyticsData?.weeklyIncome || {};
    const startOfWeek = moment().startOf('isoWeek');
    const days = Array.from({ length: 7 }).map((_, i) => startOfWeek.clone().add(i, 'days'));
    const categories = days.map((day) => day.format('DD MMM'));
    const dateKeys = days.map((day) => day.format('YYYY-MM-DD'));
    const usageData = dateKeys.map((date) => weeklyIncome[date]?.usage || 0);
    const ciroData = dateKeys.map((date) => weeklyIncome[date]?.income || 0);
    return {
      series: [
        { name: 'Kullanım Adedi', type: 'line', data: usageData },
        { name: 'Ciro', type: 'column', data: ciroData }], categories
    };
  }, [analyticsData]);

  const weeklyComboChartOptions = {
    chart: {
      type: 'line', stacked: false, toolbar: { show: false }
    },
    stroke: { width: [3, 0], curve: 'smooth' },

    markers: { size: 6, strokeWidth: 0, hover: { size: 8 } },
    colors: ['#14B8A6', '#1C64F2'],
    xaxis: { categories: weeklyComboChartData.categories },
    yaxis: [
      { seriesName: 'Kullanım Adedi', axisTicks: { show: true }, axisBorder: { show: true }, title: { text: 'Kullanım Adedi' }, labels: { formatter: (val) => val.toFixed(0) } },
      {
        seriesName: 'Ciro',
        opposite: true,
        axisTicks: { show: true },
        axisBorder: { show: true },
        title: { text: 'Ciro' },
        labels: { formatter: (val) => `${val.toFixed(0)} ₺` }
      }
    ],
    legend: { position: 'bottom', horizontalAlign: 'center' },
    tooltip: { shared: true, intersect: false },
    dataLabels: { enabled: false },
    plotOptions: { bar: { columnWidth: '50%', borderRadius: 4 } }
  };
  
  const weeklyUsageChartData = useMemo(() => {
    const weeklyIncome = analyticsData?.weeklyIncome || {};
    const startOfWeek = moment().startOf('isoWeek');
    const days = Array.from({ length: 7 }).map((_, i) => startOfWeek.clone().add(i, 'days'));
    const categories = days.map((day) => day.format('DD MMM'));
    const dateKeys = days.map((day) => day.format('YYYY-MM-DD'));
    const usageData = dateKeys.map((date) => weeklyIncome[date]?.usage || 0);
    return { series: [{ name: 'Toplam Kullanım Adedi', data: usageData }], categories };
  }, [analyticsData]);

  const weeklyOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: '45%', borderRadius: 8, borderRadiusApplication: 'end' } },
    colors: ['#1C64F2'],
    stroke: { show: true, width: 2, colors: ['#fff'] },
    xaxis: { categories: weeklyUsageChartData.categories },
    legend: { show: false },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false },
    yaxis: {
      title: {
        text: 'Toplam Kullanım Adedi'
      },
      labels: {
        formatter: (val) => val.toFixed(0),
      }
    }
  };


  const monthlyComboChart = useMemo(() => {
    const income = analyticsData?.monthlyIncome || {};
    const allMonthWeekKeys = ['1-7', '8-14', '15-21', '22-rest'];

    const categories = allMonthWeekKeys.map((range) =>
      `${currentMonthAbbrev} ${range.includes('rest') ? `${range.split('-')[0]}-${lastDayOfMonth}` : range}`
    );

    const usageData = allMonthWeekKeys.map((key) => income[key]?.usage || 0);
    const incomeData = allMonthWeekKeys.map((key) => income[key]?.income || 0);

    const series = [
      { name: 'Ciro', type: 'column', data: incomeData },
      { name: 'Kullanım Adedi', type: 'line', data: usageData },
    ];

    const options = {
      chart: { stacked: false, toolbar: { show: false } },
      stroke: { width: [0, 2] },
      plotOptions: { bar: { columnWidth: '40%' } },
      colors: ['#1C64F2', '#14B8A6'],
      xaxis: { categories },
      yaxis: [
        {
          opposite: true,
          title: { text: 'Ciro' },
          labels: { formatter: (val) => `${val.toFixed(0)} ₺` }
        },
        { title: { text: 'Kullanım Adedi' }, labels: { formatter: (val) => val.toFixed(0) } },        
      ],
      legend: { position: 'bottom' }
    };

    return { series, options };
  }, [analyticsData, currentMonthAbbrev, lastDayOfMonth]);

  // --- 13 AYLIK DİNAMİK CİRO / KULLANIM HESAPLAMASI ---
  const yearlyIncomeComboChart = useMemo(() => {
    const yearlyIncome = analyticsData?.yearlyIncome || {};
    
    const last13Months = Array.from({ length: 13 }).map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (12 - i));
      const year = d.getFullYear();
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      return `${year}-${monthStr}`;
    });

    const yearlyCategories = last13Months.map(monthStr => {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1, 1);
      const mFormat = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(date);
      const mTitle = mFormat.charAt(0).toUpperCase() + mFormat.slice(1);
      return `${mTitle} ${year}`;
    });

    const yearlyIncomeData = last13Months.map(m => yearlyIncome[m]?.income || 0);
    const yearlyUsageData = last13Months.map(m => yearlyIncome[m]?.usage || 0);

    const series = [
      { name: 'Ciro', type: 'column', data: yearlyIncomeData },
      { name: 'Kullanım Adedi', type: 'line', data: yearlyUsageData }
    ];

    const options = {
      chart: { stacked: false, toolbar: { show: false } },
      stroke: { width: [0, 2] },
      plotOptions: { bar: { columnWidth: '40%' } },
      colors: ['#1C64F2', '#14B8A6'],
      xaxis: {
        categories: yearlyCategories,
        labels: {
          rotate: -45,
          rotateAlways: false,
        }
      },
      yaxis: [
        {
          title: { text: 'Ciro' },
          labels: { formatter: (val) => `${val.toFixed(0)} ₺` }
        },
        {
          opposite: true,
          title: { text: 'Adet' },
          labels: { formatter: (val) => val.toFixed(0) }
        }
      ],
      legend: { position: 'bottom' },
      tooltip: {
        y: {
          formatter: (val, { seriesIndex }) => {
            if (seriesIndex === 0) {
              return `${val.toFixed(2)} ₺`;
            }
            return `${val.toFixed(0)} adet`;
          }
        }
      }
    };

    return { series, options };
  }, [analyticsData]);
  // ----------------------------------------------------

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Hediye Paketi</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Hediye Paketi</Typography>
        <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenImageModal}>Tanıtım Videosunu İzle</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{analyticsData?.totalDailyGiftUsage ?? 0}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Ortalama: <strong>{analyticsData?.monthlyGiftAvarageUsage ?? 0}</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon="eva:layers-outline" width={20} height={20} sx={{ color: '#fff' }} /></Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Fiyat Bilgileri</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ minWidth: '60px', fontWeight: 600 }}>Günlük:</Typography>
              <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 2, py: 0.5, textAlign: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {formatPriceWithoutVat(analyticsData?.priceList?.daily?.price, analyticsData?.priceList?.daily?.currency)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: '60px', fontWeight: 600 }}>Saatlik:</Typography>
              <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 2, py: 0.5, textAlign: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {formatPriceWithoutVat(analyticsData?.priceList?.hour?.price, analyticsData?.priceList?.hour?.currency)}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ position: 'relative', p: 3, height: 380, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6">Aylık Hediye Paketi Hizmeti Kullanımı</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi</Typography>
            <ReactApexChart options={monthlyUsageChartOptions} series={hasPermission ? monthlyUsageChartData.series : zeroOutSeries(monthlyUsageChartData.series)} type="bar" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 3, height: 380, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6">Günlük Kullanım ve Ciro Hareketleri</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={weeklyComboChartOptions} series={hasPermission ? weeklyComboChartData.series : zeroOutSeries(weeklyComboChartData.series)} type="line" height={280} />
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Haftalık Hediye Paketi Hizmeti Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi</Typography>
            <ReactApexChart options={weeklyOptions} series={hasPermission ? weeklyUsageChartData.series : zeroOutSeries(weeklyUsageChartData.series)} type="bar" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 3, height: 380, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6">Haftalık Kullanım ve Ciro Hareketleri</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart
              options={monthlyComboChart.options}
              series={hasPermission ? monthlyComboChart.series : zeroOutSeries(monthlyComboChart.series)}
              type="line"
              height={280}
            />
          </Card>
        </Grid>
      </Grid>

      {/* EKLENEN 13 AYLIK GRAFİK ALANI */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Card elevation={0} sx={{ p: 3, height: 380, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="h6">Aylık Kullanım ve Ciro Hareketleri (Son 12 Ay)</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Toplam Kullanım Adedi - Toplam Ciro</Typography>
          <ReactApexChart 
            options={yearlyIncomeComboChart.options} 
            series={hasPermission ? yearlyIncomeComboChart.series : zeroOutSeries(yearlyIncomeComboChart.series)} 
            type="line" 
            height={280} 
          />
        </Card>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Box mb={1}><Typography variant="h6">Aylık Gün ve Saat Bazlı Kullanım Yoğunluğu</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Kullanım Yoğunluğu</Typography></Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}><ReactApexChart options={dailyHeatmapChart.options} series={hasPermission ? dailyHeatmapChart.series : zeroOutSeries(dailyHeatmapChart.series)} type="heatmap" height={320} /></Box>
            <HeatmapLegend maxCount={dailyHeatmapChart.maxCount} colors={dailyHeatmapChart.colors} label="Kullanım Adedi" />
          </Stack>
        </Card>
      </Grid>

      <Paper sx={{ mt: 4, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto"><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startFilter} onChange={setStartFilter} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs="auto"><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endFilter} onChange={setEndFilter} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs="auto"><FormControl sx={{ minWidth: 120 }}><InputLabel>Durum</InputLabel><Select value={statusFilter} label="Durum" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="">Tümü</MenuItem><MenuItem value="Onaylandı">Onaylandı</MenuItem><MenuItem value="Onay Bekleniyor">Onay Bekleniyor</MenuItem></Select></FormControl></Grid>
          <Grid item xs />
          <Grid item xs="auto"><Button variant="outlined" size="medium" onClick={handleExportExcel} disabled={!hasPermission || giftList.length === 0}>EXCEL'E AKTAR</Button></Grid>
          <Grid item xs="auto"><Button variant="outlined" size="medium" onClick={handleClearFilters} disabled={!hasPermission}>TEMİZLE</Button></Grid>
          <Grid item xs="auto"><Button onClick={getLiveAnalyticsGiftList} variant="contained" size="medium" disabled={!hasPermission}>LİSTELE</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>{['NO', 'AVM', 'MÜŞTERİ ADI', 'MÜŞTERİ SOYAD', 'PAKET NO', 'PAKET ADEDİ', 'FİYAT (KDV HARİÇ)', 'OLUŞTURMA TARİHİ', 'DURUMU'].map((h) => <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {paginatedRows.map((r) => (<TableRow key={r.id} hover><TableCell>{r.id}</TableCell><TableCell>{customer?.customer?.title || 'AVM'}</TableCell><TableCell>{maskName(r.billing?.name) || '-'}</TableCell><TableCell>{maskName(r.billing?.lastname) || '-'}</TableCell><TableCell>{r.requestID}</TableCell><TableCell>{r.packageCount}</TableCell><TableCell>{formatPriceWithoutVat(r.price, r.currency || defaultGiftCurrency)}</TableCell><TableCell>{new Date(Number(r.createdCateTime) * 1000).toLocaleString('tr-TR')}</TableCell><TableCell>{STATUS_LABELS[r.status] || 'Bilinmiyor'}</TableCell></TableRow>))}
            </TableBody>
            <TableFooter><TableRow><TableCell colSpan={9} sx={{ py: 2 }}><Grid container alignItems="center"><Grid item xs><Typography variant="caption">Toplam {giftList.length} kayıt</Typography></Grid><Grid item><Pagination count={Math.ceil(giftList.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" /></Grid></Grid></TableCell></TableRow></TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isImageModalOpen} onClose={handleCloseImageModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Görseli<IconButton aria-label="close" onClick={handleCloseImageModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent><Box component="img" src="/assets/mallManagementImages/Hediye.png" alt="Tanıtım Görseli" sx={{ width: '100%', height: 'auto', borderRadius: 1 }} /></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
